import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, BACKEND_URL, shareApiPdf, openApiFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Search, FileDown, Eye, AlertTriangle, Share2, Loader2, Edit2, Copy, Ban, Download, PackagePlus } from "lucide-react";
import { ClienteCombobox } from "@/components/ClienteCombobox";
import { ProveedorCombobox } from "@/components/ProveedorCombobox";
import { FacturaCombobox } from "@/components/FacturaCombobox";
import { ProductoCombobox } from "@/components/ProductoCombobox";
import MarcarNcAnuladaSriDialog from "@/components/MarcarNcAnuladaSriDialog";
import AuditoriaXmlSri from "@/components/AuditoriaXmlSri";

/** Buscador inline reutilizable para tablas SRI complementos */
function SearchBar({ value, onChange, placeholder = "Buscar...", testId }) {
  return (
    <div className="relative w-full md:w-80">
      <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8ba397]" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-8" data-testid={testId} />
    </div>
  );
}

function matches(it, q, fields) {
  if (!q) return true;
  const s = q.toLowerCase();
  return fields.some(f => {
    const v = typeof f === "function" ? f(it) : it[f];
    return v != null && String(v).toLowerCase().includes(s);
  });
}

const EST_STYLE = {
  borrador: "bg-gray-100 text-gray-700",
  firmado: "bg-blue-100 text-blue-700",
  enviado: "bg-indigo-100 text-indigo-700",
  autorizado: "fefo-safe border",
  rechazado: "fefo-vencido border",
};

export default function SriComplementos() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "nc";
  const validTab = ["nc", "nd", "ret", "gr"].includes(tab) ? tab : "nc";
  return (
    <div className="space-y-6" data-testid="complementos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Facturación electrónica</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Complementos SRI</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Notas de crédito, retenciones — con firma digital y envío al SRI.</p>
      </div>
        <AuditoriaXmlSri />
      </div>
      <Tabs defaultValue={validTab}>
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="nc">Notas de crédito</TabsTrigger>
          <TabsTrigger value="nd">Notas de débito</TabsTrigger>
          <TabsTrigger value="ret">Retenciones</TabsTrigger>
          <TabsTrigger value="gr">Guías de remisión</TabsTrigger>
        </TabsList>
        <TabsContent value="nc"><NotasCredito /></TabsContent>
        <TabsContent value="nd"><NotasDebito /></TabsContent>
        <TabsContent value="ret"><Retenciones highlightNumero={searchParams.get("numero") || ""} /></TabsContent>
        <TabsContent value="gr"><GuiasRemision /></TabsContent>
      </Tabs>
    </div>
  );
}

const MSG_COLA_SRI = "Recibido por el SRI y en su cola de autorización (no es un error). El sistema lo reconsulta solo cada 10 minutos; a fin de mes el SRI puede tardar horas.";

function haceCuanto(iso) {
  if (!iso) return "";
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return `${min} min`;
  if (min < 1440) return `${Math.floor(min / 60)} h`;
  return `${Math.floor(min / 1440)} d`;
}

function ColaSriBadge({ item, prefix }) {
  const desde = item.enviado_sri_at;
  return (
    <span className="inline-flex items-center gap-1 rounded border border-[#e8c98a] bg-[#fff8e6] px-2 py-1 text-[11px] text-[#8a5a00]"
      title={`Última consulta: ${(item.ultima_consulta_sri_at || "—").slice(0, 16).replace("T", " ")}`}
      data-testid={`cola-sri-${prefix}-${item.id}`}>
      <Loader2 size={11} className="animate-spin" />En cola del SRI{desde ? ` · ${haceCuanto(desde)}` : ""}
    </span>
  );
}

function SriButtons({ item, prefix, onChange }) {
  const [showMessages, setShowMessages] = useState(false);
  const [accionInFlight, setAccionInFlight] = useState(null);

  const accion = async (act) => {
    setAccionInFlight(act);
    try {
      const { data } = await api.post(`/invoicing/${prefix}/${item.id}/${act}`);
      const est = data?.resultado_recepcion?.estado || data?.resultado_autorizacion?.estado;
      const msgs = data?.resultado_recepcion?.mensajes || data?.resultado_autorizacion?.mensajes || [];
      if (est === "AUTORIZADO") toast.success(`${item.numero || ""} autorizado por el SRI`);
      else if (est === "EN_PROCESAMIENTO") toast.warning(MSG_COLA_SRI, { duration: 9000 });
      else if (msgs.length > 0) toast.error(`${est}: ${msgs[0]?.mensaje || ""}`, { duration: 6000 });
      else toast.success(est || "Hecho");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAccionInFlight(null); }
  };

  // Acción combinada: ejecuta Firmar (si borrador/rechazada) → Enviar (si firmado) → Autorizar
  // en cascada, parando en el primer error con mensaje claro.
  const firmarEnviarAutorizar = async () => {
    const label = `${prefix.toUpperCase()} ${item.numero || ""}`;
    setAccionInFlight("firmar+enviar+autorizar");
    try {
      // Paso 1: Firmar (si aplica)
      if (item.estado_sri === "borrador" || item.estado_sri === "rechazado" || !item.estado_sri) {
        toast.message(`${label}: firmando...`);
        await api.post(`/invoicing/${prefix}/${item.id}/firmar`);
      }
      // Paso 2: Enviar al SRI
      toast.message(`${label}: enviando al SRI...`);
      const env = await api.post(`/invoicing/${prefix}/${item.id}/enviar`);
      const estR = env.data?.resultado_recepcion?.estado;
      const msgsR = env.data?.resultado_recepcion?.mensajes || [];
      // CLAVE ACCESO REGISTRADA (identificador 70): el SRI ya tiene el
      // comprobante registrado. NO es rechazo — significa que se envió
      // anteriormente. Saltamos a consultar autorización.
      const claveYaRegistrada = msgsR.some(m =>
        (m.identificador === "70" || m.identificador === 70) ||
        ((m.mensaje || "").toUpperCase().includes("CLAVE") && (m.mensaje || "").toUpperCase().includes("REGISTRADA"))
      );
      if (estR === "DEVUELTA" && !claveYaRegistrada) {
        toast.error(`Recepción SRI: ${msgsR.map(m => m.mensaje).join(" · ")}`, { duration: 8000 });
        onChange();
        return;
      }
      if (claveYaRegistrada) {
        toast.message(`${label}: ya estaba registrado en SRI · consultando autorización...`);
      }
      // Paso 3: Esperar 2s y consultar autorización (SRI normalmente tarda 1-3s)
      await new Promise(r => setTimeout(r, 2000));
      toast.message(`${label}: consultando autorización...`);
      const aut = await api.post(`/invoicing/${prefix}/${item.id}/autorizar`);
      const estA = aut.data?.resultado_autorizacion?.estado;
      const msgsA = aut.data?.resultado_autorizacion?.mensajes || [];
      if (estA === "AUTORIZADO") {
        toast.success(`${label} autorizado por el SRI ✓`, { duration: 5000 });
      } else if (estA === "EN_PROCESAMIENTO" || estA === "EN_PROCESO" || estA === "RECIBIDA") {
        toast.warning(`${label}: ${MSG_COLA_SRI}`, { duration: 9000 });
      } else {
        toast.error(`${label}: ${estA || ""} ${msgsA.map(m => m.mensaje).join(" · ")}`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(`Error en ${label}: ${formatApiError(e)}`, { duration: 8000 });
    } finally {
      setAccionInFlight(null);
      onChange();
    }
  };
  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar definitivamente este documento ${item.numero || ""}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/invoicing/${prefix}/${item.id}`);
      toast.success("Documento eliminado");
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const descargarPdf = async () => {
    try {
      const r = await api.get(`/invoicing/${prefix}/${item.id}/ride`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${prefix.toUpperCase()}-${item.numero || item.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // ITER225: reingresar al inventario los productos de una NC ya emitida
  const reingresarInventario = async () => {
    if (!window.confirm(`¿Reingresar al inventario los productos de la nota de crédito ${item.numero}?`)) return;
    try {
      const { data } = await api.post(`/invoicing/notas-credito/${item.id}/reingresar-inventario`);
      const n = (data.aplicado || []).length;
      toast.success(`${n} producto(s) reingresado(s) al inventario`);
      if ((data.sin_lote || []).length) {
        toast.warning(`${data.sin_lote.length} producto(s) sin lote identificado: ajústelos en Bodega`);
      }
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ITER220: XML con nombre "TIPO numero - cliente.xml"
  const descargarXml = async () => {
    const tipo = { "notas-credito": "NOTA DE CREDITO", "notas-debito": "NOTA DE DEBITO",
      "retenciones": "RETENCION", "guias-remision": "GUIA DE REMISION" }[prefix] || "COMPROBANTE";
    const cliente = (item.cliente_nombre || item.proveedor_nombre || item.razon_social_transportista
      || item.destinatarios?.[0]?.razon_social || "").replace(/[\\/:*?"<>|]+/g, " ").trim();
    const nombre = `${tipo} ${item.numero || ""}${cliente ? ` - ${cliente}` : ""}.xml`.replace(/\s{2,}/g, " ");
    try {
      await openApiFile(`/invoicing/${prefix}/${item.id}/xml`, { filename: nombre, mime: "application/xml", downloadOnly: true });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ITER216: reconciliar con el SRI usando la clave de acceso del portal
  const reconciliar = async () => {
    const clave = window.prompt(
      `El comprobante ${item.numero || ""} ya aparece AUTORIZADO en el portal del SRI?\n\n` +
      `Pegue aquí la CLAVE DE ACCESO (49 dígitos) que muestra el portal para sincronizarlo:`
    );
    if (!clave) return;
    try {
      const { data } = await api.post(`/invoicing/${prefix}/${item.id}/reconciliar-sri`, {
        clave_acceso: clave.trim(),
      });
      toast.success(`Sincronizado: AUTORIZADO · Nº ${(data.numero_autorizacion || "").slice(0, 25)}…`, { duration: 8000 });
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e), { duration: 10000 }); }
  };

  const previewPdf = async () => {
    try {
      const r = await api.get(`/invoicing/${prefix}/${item.id}/ride-preview`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const sharePdf = async () => {
    try {
      const r = await shareApiPdf(`/invoicing/${prefix}/${item.id}/ride`, {
        filename: `${prefix.toUpperCase()}-${item.numero || ""}.pdf`,
        title: `${prefix} ${item.numero || ""}`,
        text: `Adjunto documento ${item.numero || ""}.`,
      });
      if (r.fallback === "downloaded") toast.message("PDF descargado · Adjúntelo manualmente");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const mensajes = item.mensajes_sri || [];
  const hasMensajes = mensajes.length > 0;
  const isRechazado = item.estado_sri === "rechazado" || item.estado_sri === "no_autorizado";
  return (
    <div className="flex gap-1 justify-end flex-wrap">
      {(item.estado_sri === "borrador" || item.estado_sri === "rechazado" || !item.estado_sri) && (
        <Button
          size="sm"
          className="bg-[#205c45] hover:bg-[#1a4a37]"
          onClick={firmarEnviarAutorizar}
          disabled={!!accionInFlight}
          data-testid={`firmar-enviar-${prefix}-${item.id}`}
        >
          {accionInFlight === "firmar+enviar+autorizar" ? (
            <><Loader2 size={14} className="mr-1 animate-spin" />Procesando...</>
          ) : (
            <><CheckCircle2 size={14} className="mr-1" />Firmar + Enviar al SRI</>
          )}
        </Button>
      )}
      {item.estado_sri === "firmado" && (
        <Button
          size="sm"
          className="bg-[#205c45] hover:bg-[#1a4a37]"
          onClick={firmarEnviarAutorizar}
          disabled={!!accionInFlight}
          data-testid={`enviar-auto-${prefix}-${item.id}`}
        >
          {accionInFlight === "firmar+enviar+autorizar" ? (
            <><Loader2 size={14} className="mr-1 animate-spin" />Procesando...</>
          ) : (
            <><CheckCircle2 size={14} className="mr-1" />Enviar al SRI</>
          )}
        </Button>
      )}
      {prefix === "notas-credito" && (item.lineas || []).some(l => l.producto_comercial_id && Number(l.cantidad) > 0) && (
        item.inventario_reingresado ? (
          <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] text-[10px]"
            title={`Reingresado el ${(item.inventario_reingresado_at || "").slice(0, 10)}`}
            data-testid={`inv-reingresado-${item.id}`}>
            <PackagePlus size={11} className="mr-1" />Stock devuelto
          </Badge>
        ) : (
          <Button size="sm" variant="outline" onClick={reingresarInventario}
            title="Devolver al inventario los productos de esta nota de crédito"
            data-testid={`reingresar-inv-${item.id}`}>
            <PackagePlus size={14} className="mr-1" />Reingresar stock
          </Button>
        )
      )}
      {!!item.clave_acceso && (
        <Button size="sm" variant="outline" onClick={descargarXml} title="Descargar XML" data-testid={`xml-${prefix}-${item.id}`}>
          <Download size={14} className="mr-1" />XML
        </Button>
      )}
      {(item.estado_sri === "enviado") && (
        <>
          <ColaSriBadge item={item} prefix={prefix} />
          <Button size="sm" variant="outline" onClick={() => accion("autorizar")} disabled={!!accionInFlight} title="Reconsultar autorización al SRI" data-testid={`reauto-${prefix}-${item.id}`}>
            {accionInFlight === "autorizar"
              ? <><Loader2 size={14} className="mr-1 animate-spin" />Consultando...</>
              : <><CheckCircle2 size={14} className="mr-1" />Reconsultar autorización</>}
          </Button>
        </>
      )}
      {/* ITER216: el documento sí está autorizado en el portal SRI pero con otra
          clave de acceso (se regeneró el XML) → reconciliar pegando la clave */}
      {["enviado", "rechazado", "firmado"].includes(item.estado_sri) && (
        <Button size="sm" variant="outline" onClick={reconciliar}
          title="Ya está autorizado en el portal del SRI: pegue la clave de acceso para sincronizarlo"
          data-testid={`reconciliar-${prefix}-${item.id}`}>
          <CheckCircle2 size={14} className="mr-1" />Ya está en el SRI
        </Button>
      )}
      {/* Botones de PDF — disponibles para todos los estados que tengan al menos borrador (necesitan XML) */}
      {item.estado_sri && (
        <>
          <Button size="icon" variant="ghost" onClick={previewPdf} title="Vista previa PDF" data-testid={`prev-${prefix}-${item.id}`}>
            <Eye size={14} className="text-[#205c45]" />
          </Button>
          <Button size="icon" variant="ghost" onClick={descargarPdf} title="Descargar PDF" data-testid={`pdf-${prefix}-${item.id}`}>
            <FileDown size={14} className="text-[#205c45]" />
          </Button>
          <Button size="icon" variant="ghost" onClick={sharePdf} title="Compartir PDF" data-testid={`share-${prefix}-${item.id}`}>
            <Share2 size={14} className="text-[#205c45]" />
          </Button>
        </>
      )}
      {(hasMensajes || isRechazado) && (
        <Button size="icon" variant="ghost" onClick={() => setShowMessages(true)} title="Ver mensajes del SRI" data-testid={`msg-${prefix}-${item.id}`}>
          <AlertTriangle size={14} className="text-[#b53a25]" />
        </Button>
      )}
      {(item.estado_sri === "enviado") && (
        <Button size="sm" variant="outline" className="text-[#8a5500] hover:bg-[#fef7ec]" onClick={async () => {
          const ok = window.confirm(`¿Marcar este documento ${item.numero || ""} como RECHAZADO manualmente?\n\nUsar esto SOLO si el SRI devolvió ERROR EN DIFERENCIAS u otro rechazo definitivo. Después podrá eliminarlo y crear uno nuevo corregido.\n\nNo aplicar si el SRI puede autorizarlo aún.`);
          if (!ok) return;
          try {
            await api.post(`/invoicing/${prefix}/${item.id}/marcar-rechazada`);
            toast.success("Marcado como rechazado");
            onChange();
          } catch (e) { toast.error(formatApiError(e)); }
        }} title="Marcar como rechazada (SRI ERROR EN DIFERENCIAS)" data-testid={`reject-${prefix}-${item.id}`}>
          <AlertTriangle size={14} className="mr-1" /> Rechazar
        </Button>
      )}
      {(item.estado_sri === "borrador" || item.estado_sri === "rechazado" || !item.estado_sri) && (
        <Button size="sm" variant="outline" className="text-[#b53a25] hover:bg-[#fdecea]" onClick={eliminar} title="Eliminar borrador / rechazado" data-testid={`del-${prefix}-${item.id}`}>
          <Trash2 size={14} />
        </Button>
      )}
      {/* ITER201 (Feb 2026): anular con reversión COMPLETA — solo para retenciones */}
      {prefix === "retenciones" && (
        <Button
          size="sm"
          variant="outline"
          className="text-[#7a3f00] border-[#a35400] hover:bg-[#fdf3ea]"
          onClick={async () => {
            const motivo = window.prompt(
              `ANULAR RETENCIÓN ${item.numero || ""}\n\n` +
              `⚠ Esta acción REVIERTE completamente los efectos de la retención:\n` +
              `• Devuelve el valor de retención al saldo pendiente en CxP (${item.proveedor_nombre || "el proveedor"} volverá a deber el monto retenido).\n` +
              `• Limpia la referencia en la factura de compra.\n` +
              `• Elimina el asiento contable.\n` +
              `• Elimina el documento — no queda registro visible.\n\n` +
              `Indique el motivo de la anulación (obligatorio, mínimo 5 caracteres):`,
              ""
            );
            if (motivo === null) return;
            if ((motivo || "").trim().length < 5) { toast.error("El motivo es obligatorio (mínimo 5 caracteres)"); return; }
            const estadoNorm = (item.estado_sri || "").toString().trim().toLowerCase();
            const yaAutorizada = estadoNorm === "autorizada" || estadoNorm === "autorizado";
            if (yaAutorizada && !window.confirm(`⚠ Esta retención YA FUE AUTORIZADA por el SRI (${item.clave_acceso ? item.clave_acceso.slice(0, 12) + "…" : ""}).\n\nDebes gestionar por separado su anulación ante el SRI. ¿Continuar con la anulación local (?forzar=true)?`)) return;
            try {
              const url = `/purchases/retenciones/${item.id}?motivo=${encodeURIComponent(motivo.trim())}${yaAutorizada ? "&forzar=true" : ""}`;
              const { data } = await api.delete(url);
              const det = data?.detalle || {};
              let toastMsg = `Retención ${det.retencion_numero || ""} anulada · $${(det.total_retenido || 0).toFixed(2)} revertido`;
              if (det.cxp_revertida) toastMsg += ` · CxP saldo: $${det.cxp_revertida.saldo_despues?.toFixed(2)}`;
              if (det.asientos_eliminados) toastMsg += ` · ${det.asientos_eliminados} asiento(s) eliminado(s)`;
              toast.success(toastMsg, { duration: 7000 });
              onChange();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          title="Anular retención con reversión completa (CxP, factura, asientos, doc eliminado)"
          data-testid={`anular-retencion-${item.id}`}
        >
          <Ban size={14} className="mr-1" /> Anular
        </Button>
      )}
      <Dialog open={showMessages} onOpenChange={setShowMessages}>
        <DialogContent className="max-w-2xl" data-testid={`sri-msgs-dialog-${item.id}`}>
          <DialogHeader>
            <DialogTitle>Mensajes del SRI · {item.numero || ""}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-[#5c7368] mb-2">
            Estado actual: <strong className="uppercase">{item.estado_sri || "—"}</strong>
          </div>
          {mensajes.length === 0 ? (
            <p className="text-sm italic text-[#8ba397]">Sin mensajes detallados del SRI.</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {mensajes.map((m, i) => (
                <div key={i} className="border border-[#d1dbd6] rounded p-3 bg-[#fdf8f0]">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-[#b53a25] text-white text-[10px]">{m.tipo || "ERROR"}</Badge>
                    <span className="font-mono text-[11px] text-[#5c7368]">Código: {m.identificador || "—"}</span>
                  </div>
                  <p className="text-sm font-semibold text-[#15241d]">{m.mensaje || "—"}</p>
                  {m.informacionAdicional && (
                    <p className="text-xs text-[#5c7368] mt-1 whitespace-pre-wrap">
                      <strong>Detalle:</strong> {m.informacionAdicional}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessages(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotasCredito() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anularNcFc, setAnularNcFc] = useState(null);  // ITER188: NC a marcar como anulada en SRI
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  function empty() {
    return {
      cliente_id: "", fecha: new Date().toISOString().slice(0, 10),
      factura_origen_id: "", factura_origen_numero: "",
      factura_origen_fecha: new Date().toISOString().slice(0, 10),
      motivo: "Devolución de mercadería", lineas: [], descontar_comision: true, reingresar_inventario: true,
    };
  }

  const load = async () => {
    const [r, c, f, cc] = await Promise.all([
      api.get("/invoicing/notas-credito"),
      api.get("/masters/clientes"),
      api.get("/invoicing/facturas"),
      api.get("/accounting/cuentas"),
    ]);
    setItems(r.data);
    setClientes(c.data.filter(x => x.estado === "aprobado"));
    setFacturas(f.data);
    setCuentas(cc.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  // Facturas disponibles para el cliente seleccionado
  const facturasCliente = useMemo(() =>
    facturas.filter(f => f.cliente_id === form.cliente_id &&
      (f.estado_sri === "autorizado" || f.estado_sri === "enviado")),
    [facturas, form.cliente_id]
  );

  // Al elegir la factura origen, autocompletar datos y cargar sus líneas como productos
  const onSelectFactura = (factId) => {
    const f = facturas.find(x => x.id === factId);
    if (!f) return;
    const lineas = (f.lineas || []).map(l => ({
      tipo: "producto",
      producto_comercial_id: l.producto_comercial_id,
      codigo_principal: l.codigo_principal || l.codigo_interno,
      producto_nombre: l.producto_nombre || l.descripcion,
      descripcion: "",
      cantidad: l.cantidad, precio_unitario: l.precio_unitario,
      descuento: 0, subtotal: l.subtotal,
      iva_porc: l.iva_porc ?? 15,
      marca: l.marca, registro_sanitario: l.registro_sanitario,
      numero_lote: l.numero_lote, lote_id: l.lote_id,
      fecha_vencimiento: l.fecha_vencimiento,
      fecha_elaboracion: l.fecha_elaboracion,
      presentacion: l.presentacion,
      unidad_venta: l.unidad_venta,
      unidades_por_caja: l.unidades_por_caja,
      laboratorio_nombre: l.laboratorio_nombre || l.laboratorio,
      laboratorio: l.laboratorio,
      forma_farmaceutica: l.forma_farmaceutica,
      principio_activo: l.principio_activo,
      concentracion: l.concentracion,
    }));
    setForm(prev => ({
      ...prev,
      factura_origen_id: f.id,
      factura_origen_numero: f.numero,
      factura_origen_fecha: f.fecha,
      lineas,
    }));
  };

  // ITER225: el reingreso se sugiere solo cuando el motivo es una devolución
  const onMotivoChange = (v) => setForm(f => ({
    ...f, motivo: v,
    reingresar_inventario: /devoluci/i.test(v || ""),
  }));

  const addLineaProducto = () => setForm(f => ({ ...f, lineas: [...f.lineas, {
    tipo: "producto", codigo_principal: "", producto_nombre: "",
    cantidad: 1, precio_unitario: 0, descuento: 0, subtotal: 0, iva_porc: 15,
  }] }));

  const addLineaAjuste = () => setForm(f => ({ ...f, lineas: [...f.lineas, {
    tipo: "ajuste", cuenta_contable_id: "", cuenta_contable_codigo: "",
    descripcion: "Descuento", cantidad: 1, precio_unitario: 0, descuento: 0,
    subtotal: 0, iva_porc: 0,
  }] }));

  const updateLinea = (i, field, val) => setForm(f => {
    const nl = [...f.lineas];
    nl[i] = { ...nl[i], [field]: val };
    // Cuando se selecciona cuenta contable, autocompletar código
    if (field === "cuenta_contable_id") {
      const c = cuentas.find(x => x.id === val);
      if (c) {
        nl[i].cuenta_contable_codigo = c.codigo;
        if (!nl[i].descripcion || nl[i].descripcion === "Descuento") {
          nl[i].descripcion = c.nombre;
        }
      }
    }
    nl[i].subtotal = Math.max(0,
      (parseFloat(nl[i].cantidad) || 0) * (parseFloat(nl[i].precio_unitario) || 0)
      - (parseFloat(nl[i].descuento) || 0));
    return { ...f, lineas: nl };
  });

  const totales = useMemo(() => {
    const sub = form.lineas.reduce((s, l) => s + (l.subtotal || 0), 0);
    const iva = form.lineas.reduce((s, l) => s + (l.subtotal || 0) * ((l.iva_porc || 0) / 100), 0);
    return { subtotal: +sub.toFixed(2), iva: +iva.toFixed(2), total: +(sub + iva).toFixed(2) };
  }, [form.lineas]);

  const save = async () => {
    if (!form.cliente_id || form.lineas.length === 0) return toast.error("Complete cliente y al menos una línea");
    if (!form.factura_origen_numero) return toast.error("La factura origen es obligatoria (requisito SRI)");
    try {
      if (editingId) {
        await api.put(`/invoicing/notas-credito/${editingId}`, { ...form, ...totales });
        toast.success("Nota de crédito actualizada");
      } else {
        await api.post("/invoicing/notas-credito", { ...form, ...totales });
        toast.success("Nota de crédito creada (borrador)");
      }
      setDialogOpen(false); setEditingId(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editar = (nc) => {
    setEditingId(nc.id);
    setForm({
      cliente_id: nc.cliente_id || "",
      fecha: nc.fecha || new Date().toISOString().slice(0, 10),
      factura_origen_id: nc.factura_origen_id || "",
      factura_origen_numero: nc.factura_origen_numero || "",
      factura_origen_fecha: nc.factura_origen_fecha || new Date().toISOString().slice(0, 10),
      motivo: nc.motivo || "Devolución de mercadería",
      descontar_comision: !!nc.descontar_comision,
      reingresar_inventario: nc.reingresar_inventario !== undefined ? !!nc.reingresar_inventario : true,
      lineas: (nc.lineas || []).map(l => ({
        tipo: l.tipo || "producto",
        producto_comercial_id: l.producto_comercial_id || null,
        codigo_principal: l.codigo_principal,
        producto_nombre: l.producto_nombre,
        cuenta_contable_id: l.cuenta_contable_id || "",
        cuenta_contable_codigo: l.cuenta_contable_codigo || "",
        descripcion: l.descripcion || "",
        cantidad: l.cantidad || 0,
        precio_unitario: l.precio_unitario || 0,
        descuento: l.descuento || 0,
        subtotal: l.subtotal || 0,
        iva_porc: l.iva_porc ?? 0,
        marca: l.marca, registro_sanitario: l.registro_sanitario,
        numero_lote: l.numero_lote, lote_id: l.lote_id,
        fecha_vencimiento: l.fecha_vencimiento,
        fecha_elaboracion: l.fecha_elaboracion,
        presentacion: l.presentacion,
        unidad_venta: l.unidad_venta,
        unidades_por_caja: l.unidades_por_caja,
        laboratorio_nombre: l.laboratorio_nombre || l.laboratorio,
        laboratorio: l.laboratorio,
        forma_farmaceutica: l.forma_farmaceutica,
        principio_activo: l.principio_activo,
        concentracion: l.concentracion,
      })),
    });
    setDialogOpen(true);
  };

  const duplicar = async (nc) => {
    try {
      const { data } = await api.get(`/invoicing/notas-credito/${nc.id}/duplicar`);
      setEditingId(null); // será una NC nueva (no edit)
      setForm({
        cliente_id: data.cliente_id || "",
        fecha: data.fecha || new Date().toISOString().slice(0, 10),
        factura_origen_id: data.factura_origen_id || "",
        factura_origen_numero: data.factura_origen_numero || "",
        factura_origen_fecha: data.factura_origen_fecha || new Date().toISOString().slice(0, 10),
        motivo: data.motivo || "Devolución de mercadería",
        lineas: (data.lineas || []).map(l => ({
          tipo: l.tipo || "producto",
          producto_comercial_id: l.producto_comercial_id || null,
          codigo_principal: l.codigo_principal,
          producto_nombre: l.producto_nombre,
          cuenta_contable_id: l.cuenta_contable_id || "",
          cuenta_contable_codigo: l.cuenta_contable_codigo || "",
          descripcion: l.descripcion || "",
          cantidad: l.cantidad || 0,
          precio_unitario: l.precio_unitario || 0,
          descuento: l.descuento || 0,
          subtotal: l.subtotal || 0,
          iva_porc: l.iva_porc ?? 0,
          marca: l.marca, registro_sanitario: l.registro_sanitario,
          numero_lote: l.numero_lote,
          presentacion: l.presentacion,
          unidad_venta: l.unidad_venta,
          unidades_por_caja: l.unidades_por_caja,
          laboratorio_nombre: l.laboratorio_nombre || l.laboratorio,
          laboratorio: l.laboratorio,
          forma_farmaceutica: l.forma_farmaceutica,
          principio_activo: l.principio_activo,
          concentracion: l.concentracion,
        })),
      });
      setDialogOpen(true);
      toast.info(`Duplicada desde NC ${data._duplicado_de?.numero || ""}. Revisa y guarda como nueva.`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cerrarDialog = (open) => {
    setDialogOpen(open);
    if (!open) { setEditingId(null); setForm(empty()); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar (número, cliente, factura origen)…" testId="nc-search" />
        <Button onClick={() => setDialogOpen(true)} className="bg-[#205c45]" data-testid="create-nc-btn"><Plus size={16} className="mr-2" />Nueva nota de crédito</Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Fact. origen</th><th className="text-right">Total</th><th>Estado</th><th className="w-48"></th></tr></thead>
          <tbody>
            {items.filter(it => matches(it, q, ["numero", "cliente_nombre", "factura_origen_numero", "estado_sri"])).map(it => (
              <tr key={it.id}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td>{it.cliente_nombre}</td>
                <td className="font-mono text-xs">{it.factura_origen_numero}</td>
                <td className="text-right tabular-nums font-semibold">${it.total?.toFixed(2)}</td>
                <td><Badge className={`${EST_STYLE[it.estado_sri] || ""} capitalize`}>{it.estado_sri}</Badge></td>
                <td>
                  <div className="flex gap-1 items-center">
                    {(it.estado_sri === "borrador") && (
                      <Button size="sm" variant="outline" onClick={() => editar(it)} data-testid={`edit-nc-${it.numero}`} className="text-[#205c45] border-[#205c45] hover:bg-[#e9f2ee]">
                        <Edit2 size={14} className="mr-1" />Editar
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => duplicar(it)} title="Duplicar como nueva NC en borrador" data-testid={`duplicate-nc-${it.numero}`} className="text-[#5c2b85] border-[#5c2b85] hover:bg-[#f4ecfa]">
                      <Copy size={14} className="mr-1" />Duplicar
                    </Button>
                    {!["anulado", "anulada"].includes((it.estado_sri || "").toLowerCase()) && (
                      <Button size="sm" variant="outline" onClick={() => setAnularNcFc(it)} title="Marcar como anulada en SRI y revertir stock/CxC/comisión" data-testid={`anular-nc-sri-${it.numero}`} className="text-[#b53a25] border-[#b53a25] hover:bg-[#fdecea]">
                        <Ban size={14} className="mr-1" />Anular SRI
                      </Button>
                    )}
                    <SriButtons item={it} prefix="notas-credito" onChange={load} />
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin notas de crédito</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={cerrarDialog}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar nota de crédito" : "Nueva nota de crédito"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Cliente *</Label>
              <ClienteCombobox
                value={form.cliente_id}
                onChange={(v) => setForm({ ...form, cliente_id: v, factura_origen_id: "", factura_origen_numero: "", factura_origen_fecha: new Date().toISOString().slice(0, 10), lineas: [] })}
                clientes={clientes}
                placeholder="Buscar por razón social, RUC o ciudad…"
                testId="nc-cliente"
              />
            </div>
            <div><Label>Fecha NC</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></div>

            <div className="col-span-3 bg-[#e9f2ee] border border-[#b2d0c2] rounded p-3">
              <Label className="text-xs uppercase tracking-wider text-[#205c45] font-semibold">Factura origen (requisito SRI) *</Label>
              {form.cliente_id && facturasCliente.length > 0 ? (
                <FacturaCombobox
                  value={form.factura_origen_id}
                  onChange={onSelectFactura}
                  facturas={facturasCliente}
                  placeholder="Buscar factura del cliente por número, fecha o total…"
                  testId="nc-factura"
                  className="mt-1"
                />
              ) : (
                <div className="text-xs text-[#5c7368] italic mt-1">
                  {!form.cliente_id ? "Primero elija un cliente" : "Este cliente no tiene facturas autorizadas; puede ingresar el número manualmente abajo"}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label className="text-xs">N° Factura</Label><Input value={form.factura_origen_numero} onChange={(e) => setForm({ ...form, factura_origen_numero: e.target.value })} placeholder="001-002-000000001" /></div>
                <div><Label className="text-xs">Fecha factura</Label><Input type="date" value={form.factura_origen_fecha} onChange={(e) => setForm({ ...form, factura_origen_fecha: e.target.value })} /></div>
              </div>
            </div>

            <div className="col-span-3"><Label>Motivo</Label>
              <Select value={form.motivo} onValueChange={onMotivoChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Devolución de mercadería">Devolución de mercadería</SelectItem>
                  <SelectItem value="Descuento por pronto pago">Descuento por pronto pago</SelectItem>
                  <SelectItem value="Descuento comercial">Descuento comercial</SelectItem>
                  <SelectItem value="Corrección de precio">Corrección de precio</SelectItem>
                  <SelectItem value="Bonificación">Bonificación</SelectItem>
                  <SelectItem value="Otros">Otros (especificar en observación)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ITER205: confirmación explícita de descuento al vendedor */}
          <div className={`mt-3 rounded-md border px-3 py-2.5 flex items-start gap-3 ${form.descontar_comision ? "border-[#d99584] bg-[#fdecea]" : "border-[#d1dbd6] bg-[#f7f9f8]"}`}>
            <input
              type="checkbox"
              id="nc-descontar-comision"
              className="mt-0.5 h-4 w-4 accent-[#7a2514]"
              checked={!!form.descontar_comision}
              onChange={(e) => setForm({ ...form, descontar_comision: e.target.checked })}
              data-testid="nc-descontar-comision"
            />
            <label htmlFor="nc-descontar-comision" className="text-xs leading-relaxed cursor-pointer">
              <span className="font-semibold text-[#15241d]">Descontar esta nota de crédito al vendedor</span>
              <div className="text-[#5c7368] mt-0.5">
                Viene marcado por defecto: la NC se restará de la comisión del vendedor y aparecerá en
                “Descuentos por devoluciones”. <strong>Desmárquelo</strong> si esta nota de crédito NO debe
                afectar su comisión (siempre podrá añadirla o quitarla después desde Comisiones).
              </div>
            </label>
          </div>

          {/* ITER225: reingreso de la mercadería devuelta al inventario */}
          <div className={`mt-3 rounded-md border px-3 py-2.5 flex items-start gap-3 ${form.reingresar_inventario ? "border-[#b2d0c2] bg-[#e9f2ee]" : "border-[#d1dbd6] bg-[#f7f9f8]"}`}>
            <input
              type="checkbox"
              id="nc-reingresar-inventario"
              className="mt-0.5 h-4 w-4 accent-[#205c45]"
              checked={!!form.reingresar_inventario}
              onChange={(e) => setForm({ ...form, reingresar_inventario: e.target.checked })}
              data-testid="nc-reingresar-inventario"
            />
            <label htmlFor="nc-reingresar-inventario" className="text-xs leading-relaxed cursor-pointer">
              <span className="font-semibold text-[#15241d]">Reingresar la mercadería al inventario</span>
              <div className="text-[#5c7368] mt-0.5">
                Marcado por defecto en devoluciones: al guardar, los productos vuelven al mismo lote de la
                factura de origen. <strong>Desmárquelo</strong> si la nota de crédito es por descuento, error de
                precio o si la mercadería no regresó a bodega.
              </div>
            </label>
          </div>

          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Líneas de la nota de crédito</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={addLineaProducto} data-testid="add-producto"><Plus size={14} className="mr-1" />Producto devuelto</Button>
                <Button size="sm" variant="outline" onClick={addLineaAjuste} data-testid="add-ajuste"><Plus size={14} className="mr-1" />Ajuste / Descuento</Button>
              </div>
            </div>
            <table className="erp-table">
              <thead>
                <tr>
                  <th className="w-20">Tipo</th>
                  <th className="w-28">Código / Cuenta</th>
                  <th>Descripción</th>
                  <th className="w-20">Cant.</th>
                  <th className="w-28">Precio U.</th>
                  <th className="w-20">IVA%</th>
                  <th className="w-24 text-right">Subtotal</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.lineas.map((l, i) => (
                  <tr key={`ret-l-${i}-${l.codigo_interno || l.tipo}`} className={l.tipo === "ajuste" ? "bg-[#fef7ec]" : ""}>
                    <td>
                      <Badge className={l.tipo === "ajuste"
                        ? "bg-[#fef7ec] text-[#8a5500] border border-[#f5c477] text-[10px]"
                        : "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] text-[10px]"}>
                        {l.tipo === "ajuste" ? "Ajuste" : "Producto"}
                      </Badge>
                    </td>
                    <td>
                      {l.tipo === "ajuste" ? (
                        <Select value={l.cuenta_contable_id || ""} onValueChange={(v) => updateLinea(i, "cuenta_contable_id", v)}>
                          <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Cuenta..." /></SelectTrigger>
                          <SelectContent>{cuentas.map(c => <SelectItem key={c.id} value={c.id}>{c.codigo} · {c.nombre}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input className="h-9 text-xs" value={l.codigo_principal || ""} onChange={(e) => updateLinea(i, "codigo_principal", e.target.value)} />
                      )}
                    </td>
                    <td>
                      <Input className="h-9 text-xs"
                        value={l.tipo === "ajuste" ? (l.descripcion || "") : (l.producto_nombre || "")}
                        onChange={(e) => updateLinea(i, l.tipo === "ajuste" ? "descripcion" : "producto_nombre", e.target.value)}
                        placeholder={l.tipo === "ajuste" ? "Ej: Descuento por pronto pago" : "Nombre producto"} />
                    </td>
                    <td><Input className="h-9" type="number" value={l.cantidad} onChange={(e) => updateLinea(i, "cantidad", parseFloat(e.target.value) || 0)} /></td>
                    <td><Input className="h-9" type="number" step="0.01" value={l.precio_unitario} onChange={(e) => updateLinea(i, "precio_unitario", parseFloat(e.target.value) || 0)} /></td>
                    <td><Input className="h-9 w-16 text-center tabular-nums font-semibold" type="number" min="0" step="1" value={l.iva_porc} onChange={(e) => updateLinea(i, "iva_porc", parseFloat(e.target.value) || 0)} data-testid={`nc-iva-${i}`} /></td>
                    <td className="text-right font-semibold tabular-nums">${l.subtotal.toFixed(2)}</td>
                    <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({ ...f, lineas: f.lineas.filter((_, x) => x !== i) }))}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
                {form.lineas.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-6 text-[#8ba397] text-xs italic">Seleccione una factura origen para cargar sus productos, o agregue manualmente líneas de producto/ajuste</td></tr>
                )}
              </tbody>
              <tfoot className="bg-[#f7f9f8] border-t">
                <tr>
                  <td colSpan={6} className="text-right uppercase text-xs font-semibold text-[#5c7368] py-2 px-3">Subtotal / IVA / Total</td>
                  <td colSpan={2} className="text-right font-bold tabular-nums py-2 px-3">
                    <div className="text-xs text-[#5c7368]">${totales.subtotal.toFixed(2)} + IVA ${totales.iva.toFixed(2)}</div>
                    <div className="text-lg">${totales.total.toFixed(2)}</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => cerrarDialog(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45]" data-testid="save-nc-btn">{editingId ? "Guardar cambios" : "Generar NC"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MarcarNcAnuladaSriDialog
        nc={anularNcFc}
        onClose={() => setAnularNcFc(null)}
        onSaved={() => { setAnularNcFc(null); load(); }}
      />
    </div>
  );
}

function Retenciones({ highlightNumero }) {
  const [items, setItems] = useState([]);
  const [provs, setProvs] = useState([]);
  const [facturasCompra, setFacturasCompra] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [q, setQ] = useState(highlightNumero || "");
  const [form, setForm] = useState(empty());
  const rowRefs = useRef({});
  function empty() { return { proveedor_id: "", fecha: new Date().toISOString().slice(0,10), factura_proveedor_id: "", factura_proveedor_numero: "", factura_proveedor_fecha: new Date().toISOString().slice(0,10), periodo_fiscal: new Date().toISOString().slice(0,7).replace("-","/"), impuestos: [] }; }

  const load = async () => {
    const [r, p, fc] = await Promise.all([
      api.get("/invoicing/retenciones"),
      api.get("/masters/proveedores"),
      api.get("/purchases/facturas").catch(() => ({ data: [] })),
    ]);
    setItems(r.data); setProvs(p.data); setFacturasCompra(fc.data || []);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  // Si llegan con ?numero=XXX, hacer scroll y resaltar
  useEffect(() => {
    if (!highlightNumero || items.length === 0) return;
    const t = setTimeout(() => {
      const el = rowRefs.current[highlightNumero];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#205c45]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#205c45]", "ring-offset-2"), 3500);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [highlightNumero, items]);

  const facturasProv = useMemo(
    () => facturasCompra
      .filter(f => f.proveedor_id === form.proveedor_id)
      .map(f => ({
        id: f.id,
        numero: f.numero_factura || f.numero,
        fecha: f.fecha_factura || f.fecha,
        total: f.total || f.valor_total || 0,
        cliente_nombre: f.proveedor_nombre,
        lineas: f.lineas || [],
      })),
    [facturasCompra, form.proveedor_id]
  );

  const onSelectFacturaProv = (factId) => {
    const fr = facturasCompra.find(x => x.id === factId);
    if (!fr) return;
    const subtotal = fr.subtotal || fr.total_sin_impuestos || (fr.total ? fr.total / 1.15 : 0);
    setForm(prev => {
      // Si no hay impuestos, prepoblar con uno de Renta 1% sobre subtotal
      const imps = prev.impuestos.length > 0 ? prev.impuestos : [{
        codigo: "1", codigo_retencion: "312",
        base_imponible: +(subtotal).toFixed(2),
        porcentaje: 1,
        valor_retenido: +(subtotal * 0.01).toFixed(2),
      }];
      return {
        ...prev,
        factura_proveedor_id: fr.id,
        factura_proveedor_numero: fr.numero_factura || fr.numero || "",
        factura_proveedor_fecha: fr.fecha_factura || fr.fecha || prev.factura_proveedor_fecha,
        impuestos: imps,
      };
    });
  };

  const addImp = () => setForm(f => ({...f, impuestos: [...f.impuestos, { codigo: "1", codigo_retencion: "312", base_imponible: 0, porcentaje: 1, valor_retenido: 0 }]}));
  const updImp = (i, field, val) => setForm(f => {
    const n = [...f.impuestos]; n[i] = {...n[i], [field]: val};
    n[i].valor_retenido = +(((parseFloat(n[i].base_imponible)||0) * (parseFloat(n[i].porcentaje)||0) / 100).toFixed(2));
    return {...f, impuestos: n};
  });

  const save = async () => {
    if (!form.proveedor_id || form.impuestos.length === 0) return toast.error("Complete proveedor y al menos un impuesto");
    try { await api.post("/invoicing/retenciones", form); toast.success("Retención creada (borrador)"); setDialogOpen(false); setForm(empty()); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar (número, proveedor, factura)…" testId="ret-search" />
        <Button onClick={() => setDialogOpen(true)} className="bg-[#205c45]" data-testid="create-ret-btn"><Plus size={16} className="mr-2" />Nueva retención</Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Proveedor</th><th>Periodo</th><th>Fact.</th><th className="text-right">Retenido</th><th>Estado</th><th className="w-48"></th></tr></thead>
          <tbody>
            {items.filter(it => matches(it, q, ["numero", "proveedor_nombre", "factura_proveedor_numero", "estado_sri", "periodo_fiscal"])).map(it => (
              <tr
                key={it.id}
                ref={(el) => { if (el && it.numero) rowRefs.current[it.numero] = el; }}
                className="transition-shadow rounded"
              >
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td>{it.proveedor_nombre}</td>
                <td>{it.periodo_fiscal}</td>
                <td className="font-mono text-xs">{it.factura_proveedor_numero}</td>
                <td className="text-right tabular-nums font-semibold">${it.total_retenido?.toFixed(2)}</td>
                <td><Badge className={`${EST_STYLE[it.estado_sri] || ""} capitalize`}>{it.estado_sri}</Badge></td>
                <td><SriButtons item={it} prefix="retenciones" onChange={load} /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8ba397]">Sin retenciones</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva retención</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Proveedor</Label><Select value={form.proveedor_id} onValueChange={(v) => setForm({...form, proveedor_id: v})}><SelectTrigger><SelectValue placeholder="..." /></SelectTrigger><SelectContent>{provs.map(p => <SelectItem key={p.id} value={p.id}>{p.razon_social}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({...form, fecha: e.target.value})} /></div>
            <div><Label>Factura proveedor #</Label><Input value={form.factura_proveedor_numero} onChange={(e) => setForm({...form, factura_proveedor_numero: e.target.value})} placeholder="001-001-000000001" /></div>
            <div><Label>Fecha factura</Label><Input type="date" value={form.factura_proveedor_fecha} onChange={(e) => setForm({...form, factura_proveedor_fecha: e.target.value})} /></div>
            <div><Label>Periodo fiscal (MM/YYYY)</Label><Input value={form.periodo_fiscal} onChange={(e) => setForm({...form, periodo_fiscal: e.target.value})} /></div>
          </div>
          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Impuestos retenidos</div>
              <Button size="sm" variant="outline" onClick={addImp}><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th className="w-20">Tipo</th><th className="w-24">Código ret.</th><th className="w-32">Base</th><th className="w-20">%</th><th className="w-28 text-right">Valor</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.impuestos.map((m, i) => (
                  <tr key={`imp-${i}-${m.codigo_retencion || m.codigo}`}>
                    <td><Select value={m.codigo} onValueChange={(v) => updImp(i, "codigo", v)}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">Renta</SelectItem><SelectItem value="2">IVA</SelectItem></SelectContent></Select></td>
                    <td><Input className="h-9" value={m.codigo_retencion} onChange={(e) => updImp(i, "codigo_retencion", e.target.value)} /></td>
                    <td><Input className="h-9" type="number" step="0.01" value={m.base_imponible} onChange={(e) => updImp(i, "base_imponible", parseFloat(e.target.value)||0)} /></td>
                    <td><Input className="h-9" type="number" step="0.01" value={m.porcentaje} onChange={(e) => updImp(i, "porcentaje", parseFloat(e.target.value)||0)} /></td>
                    <td className="text-right font-semibold tabular-nums">${m.valor_retenido.toFixed(2)}</td>
                    <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({...f, impuestos: f.impuestos.filter((_, x) => x !== i)}))}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-ret-btn">Generar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function NotasDebito() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(empty());
  function empty() {
    return { cliente_id: "", fecha: new Date().toISOString().slice(0, 10), factura_origen_id: "", factura_origen_numero: "", factura_origen_fecha: new Date().toISOString().slice(0, 10), motivos: [{ razon: "Intereses por mora", valor: 0 }], iva_porc: 15 };
  }
  const load = async () => {
    const [r, c, f] = await Promise.all([
      api.get("/invoicing/notas-debito"),
      api.get("/masters/clientes"),
      api.get("/invoicing/facturas?estado=autorizado"),
    ]);
    setItems(r.data); setClientes(c.data); setFacturas(f.data || []);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const facturasCliente = useMemo(
    () => facturas.filter(f => f.cliente_id === form.cliente_id && ["autorizado", "enviado"].includes(f.estado_sri)),
    [facturas, form.cliente_id]
  );

  const onSelectFactura = (factId) => {
    const f = facturas.find(x => x.id === factId);
    if (!f) return;
    setForm(prev => ({
      ...prev,
      factura_origen_id: f.id,
      factura_origen_numero: f.numero,
      factura_origen_fecha: f.fecha,
    }));
  };

  const totales = useMemo(() => {
    const base = form.motivos.reduce((s, m) => s + (parseFloat(m.valor) || 0), 0);
    const iva = base * (form.iva_porc / 100);
    return { base: +base.toFixed(2), iva: +iva.toFixed(2), total: +(base + iva).toFixed(2) };
  }, [form.motivos, form.iva_porc]);

  const addMotivo = () => setForm(f => ({ ...f, motivos: [...f.motivos, { razon: "", valor: 0 }] }));
  const updM = (i, field, val) => setForm(f => { const n = [...f.motivos]; n[i] = { ...n[i], [field]: val }; return { ...f, motivos: n }; });

  const save = async () => {
    if (!form.cliente_id || form.motivos.length === 0) return toast.error("Complete cliente y al menos un motivo");
    try {
      await api.post("/invoicing/notas-debito", {
        cliente_id: form.cliente_id, fecha: form.fecha,
        factura_origen_numero: form.factura_origen_numero, factura_origen_fecha: form.factura_origen_fecha,
        total_sin_impuestos: totales.base, valor_total: totales.total,
        impuestos: [{ codigo: "2", codigo_porcentaje: "4", tarifa: form.iva_porc, base_imponible: totales.base, valor: totales.iva }],
        motivos: form.motivos,
      });
      toast.success("Nota de débito creada (borrador)"); setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar (número, cliente, factura origen)…" testId="nd-search" />
        <Button onClick={() => setOpen(true)} className="bg-[#205c45]" data-testid="create-nd-btn"><Plus size={16} className="mr-2" />Nueva nota de débito</Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Fact. origen</th><th className="text-right">Total</th><th>Estado</th><th className="w-48"></th></tr></thead>
          <tbody>
            {items.filter(it => matches(it, q, ["numero", "cliente_nombre", "factura_origen_numero", "estado_sri"])).map(it => (
              <tr key={it.id}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td>{it.cliente_nombre}</td>
                <td className="font-mono text-xs">{it.factura_origen_numero}</td>
                <td className="text-right tabular-nums font-semibold">${it.valor_total?.toFixed(2)}</td>
                <td><Badge className={`${EST_STYLE[it.estado_sri] || ""} capitalize`}>{it.estado_sri}</Badge></td>
                <td><SriButtons item={it} prefix="notas-debito" onChange={load} /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin notas de débito</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva nota de débito</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Cliente *</Label>
              <ClienteCombobox
                value={form.cliente_id}
                onChange={(v) => setForm({ ...form, cliente_id: v, factura_origen_id: "", factura_origen_numero: "", factura_origen_fecha: new Date().toISOString().slice(0, 10) })}
                clientes={clientes}
                placeholder="Buscar por razón social, RUC o ciudad…"
                testId="nd-cliente"
              />
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></div>

            <div className="col-span-3 bg-[#e9f2ee] border border-[#b2d0c2] rounded p-3">
              <Label className="text-xs uppercase tracking-wider text-[#205c45] font-semibold">Factura origen (asociada al cliente) *</Label>
              {form.cliente_id && facturasCliente.length > 0 ? (
                <FacturaCombobox
                  value={form.factura_origen_id}
                  onChange={onSelectFactura}
                  facturas={facturasCliente}
                  placeholder="Buscar factura del cliente por número, fecha o total…"
                  testId="nd-factura"
                  className="mt-1"
                />
              ) : (
                <div className="text-xs text-[#5c7368] italic mt-1">
                  {!form.cliente_id ? "Primero elija un cliente" : "Este cliente no tiene facturas autorizadas; ingrese el número manualmente"}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label className="text-xs">N° Factura</Label><Input value={form.factura_origen_numero} onChange={(e) => setForm({ ...form, factura_origen_numero: e.target.value })} placeholder="001-002-000000001" /></div>
                <div><Label className="text-xs">Fecha factura</Label><Input type="date" value={form.factura_origen_fecha} onChange={(e) => setForm({ ...form, factura_origen_fecha: e.target.value })} /></div>
              </div>
            </div>
            <div><Label>IVA %</Label><Input type="number" value={form.iva_porc} onChange={(e) => setForm({ ...form, iva_porc: parseFloat(e.target.value) || 0 })} /></div>
          </div>
          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Motivos del débito</div>
              <Button size="sm" variant="outline" onClick={addMotivo}><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th>Razón</th><th className="w-32 text-right">Valor</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.motivos.map((m, i) => (
                  <tr key={`mot-${i}-${(m.razon || "").slice(0,8)}`}>
                    <td><Input className="h-9" value={m.razon} onChange={(e) => updM(i, "razon", e.target.value)} /></td>
                    <td><Input className="h-9 text-right" type="number" step="0.01" value={m.valor} onChange={(e) => updM(i, "valor", parseFloat(e.target.value) || 0)} /></td>
                    <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({ ...f, motivos: f.motivos.filter((_, x) => x !== i) }))}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#f7f9f8] border-t">
                <tr><td className="text-right uppercase text-xs font-semibold text-[#5c7368] py-1 px-3">Base</td><td colSpan={2} className="text-right px-3 py-1">${totales.base.toFixed(2)}</td></tr>
                <tr><td className="text-right uppercase text-xs font-semibold text-[#5c7368] py-1 px-3">IVA</td><td colSpan={2} className="text-right px-3 py-1">${totales.iva.toFixed(2)}</td></tr>
                <tr><td className="text-right uppercase text-xs font-bold text-[#5c7368] py-2 px-3">Total</td><td colSpan={2} className="text-right font-bold text-lg tabular-nums px-3 py-2">${totales.total.toFixed(2)}</td></tr>
              </tfoot>
            </table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-nd-btn">Generar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GuiasRemision() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [prods, setProds] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(empty());
  function empty() {
    const hoy = new Date().toISOString().slice(0, 10);
    return {
      fecha: hoy, dir_partida: "García Avilés 520 e/Aguirre y Luque. Piso 3. Of. 303.",
      razon_social_transportista: "", tipo_identificacion_transportista: "ruc",
      ruc_transportista: "", placa: "",
      fecha_ini_transporte: hoy, fecha_fin_transporte: hoy,
      destinatarios: [{ cliente_id: "", identificacion: "", razon_social: "", direccion: "", motivo_traslado: "Venta", ruta: "", cod_doc_sustento: "01", num_doc_sustento: "", factura_sustento_id: "", fecha_emision_doc_sustento: hoy, detalles: [] }],
    };
  }
  const load = async () => {
    const [r, c, p, f] = await Promise.all([
      api.get("/invoicing/guias-remision"),
      api.get("/masters/clientes"),
      api.get("/masters/productos-comerciales"),
      api.get("/invoicing/facturas?estado=autorizado"),
    ]);
    setItems(r.data); setClientes(c.data); setProds(p.data); setFacturas(f.data || []);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const facturasPorCliente = (cliId) =>
    facturas.filter(f => f.cliente_id === cliId && ["autorizado", "enviado"].includes(f.estado_sri));

  const updDest = (i, field, val) => setForm(f => { const d = [...f.destinatarios]; d[i] = { ...d[i], [field]: val }; return { ...f, destinatarios: d }; });

  const onCli = (i, v) => {
    const c = clientes.find(x => x.id === v);
    setForm(f => {
      const d = [...f.destinatarios];
      d[i] = {
        ...d[i],
        cliente_id: v,
        identificacion: c?.numero_identificacion || "",
        razon_social: c?.razon_social || "",
        direccion: c?.direccion_fiscal || (c?.direcciones_entrega || [])[0]?.direccion || "",
        // Reset factura sustento al cambiar cliente
        factura_sustento_id: "",
        num_doc_sustento: "",
        detalles: [],
      };
      return { ...f, destinatarios: d };
    });
  };

  const onSelectFacturaSustento = (i, factId) => {
    const fact = facturas.find(x => x.id === factId);
    if (!fact) return;
    const detalles = (fact.lineas || []).map(l => ({
      producto: l.producto_comercial_id,
      codigo_interno: l.codigo_interno || l.codigo_principal || "",
      descripcion: l.producto_nombre || l.descripcion || "",
      cantidad: l.cantidad || 1,
    }));
    setForm(f => {
      const d = [...f.destinatarios];
      d[i] = {
        ...d[i],
        factura_sustento_id: fact.id,
        num_doc_sustento: fact.numero,
        fecha_emision_doc_sustento: fact.fecha,
        cod_doc_sustento: "01",
        // ITER227: si la factura tiene dirección de entrega elegida, se usa en la guía
        direccion: fact.direccion_entrega || d[i]?.direccion || "",
        detalles,
      };
      return { ...f, destinatarios: d };
    });
  };

  const addLinea = (i) => setForm(f => { const d = [...f.destinatarios]; d[i] = { ...d[i], detalles: [...d[i].detalles, { codigo_interno: "", descripcion: "", cantidad: 1 }] }; return { ...f, destinatarios: d }; });
  const updLinea = (di, li, field, val) => setForm(f => {
    const d = [...f.destinatarios]; const det = [...d[di].detalles];
    det[li] = { ...det[li], [field]: val };
    if (field === "producto") {
      const p = prods.find(x => x.id === val);
      if (p) { det[li].codigo_interno = p.codigo_interno; det[li].descripcion = p.nombre_comercial; }
    }
    d[di] = { ...d[di], detalles: det }; return { ...f, destinatarios: d };
  });

  const save = async () => {
    if (!form.ruc_transportista || !form.placa) return toast.error("Complete transportista y placa");
    if (form.destinatarios.some(d => !d.identificacion || !d.razon_social || d.detalles.length === 0)) return toast.error("Cada destinatario necesita identificación, razón social y al menos un producto");
    try { await api.post("/invoicing/guias-remision", form); toast.success("Guía de remisión creada (borrador)"); setOpen(false); setForm(empty()); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar (número, transportista, placa)…" testId="gr-search" />
        <Button onClick={() => setOpen(true)} className="bg-[#205c45]" data-testid="create-gr-btn"><Plus size={16} className="mr-2" />Nueva guía de remisión</Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Transportista</th><th>Placa</th><th>Destinatarios</th><th>Estado</th><th className="w-48"></th></tr></thead>
          <tbody>
            {items.filter(it => matches(it, q, ["numero", "razon_social_transportista", "placa", "estado_sri"])).map(it => (
              <tr key={it.id}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td className="max-w-[200px] truncate" title={it.razon_social_transportista}>{it.razon_social_transportista}</td>
                <td className="font-mono text-xs">{it.placa}</td>
                <td className="text-xs">{it.destinatarios?.length} destinatario(s)</td>
                <td><Badge className={`${EST_STYLE[it.estado_sri] || ""} capitalize`}>{it.estado_sri}</Badge></td>
                <td><SriButtons item={it} prefix="guias-remision" onChange={load} /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin guías de remisión</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva guía de remisión</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Fecha emisión</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></div>
            <div><Label>Inicio transporte</Label><Input type="date" value={form.fecha_ini_transporte} onChange={(e) => setForm({ ...form, fecha_ini_transporte: e.target.value })} /></div>
            <div><Label>Fin transporte</Label><Input type="date" value={form.fecha_fin_transporte} onChange={(e) => setForm({ ...form, fecha_fin_transporte: e.target.value })} /></div>
            <div className="col-span-3"><Label>Dirección de partida</Label><Input value={form.dir_partida} onChange={(e) => setForm({ ...form, dir_partida: e.target.value })} /></div>
            <div><Label>Transportista (razón social)</Label><Input value={form.razon_social_transportista} onChange={(e) => setForm({ ...form, razon_social_transportista: e.target.value })} /></div>
            <div><Label>RUC/CI transportista</Label><Input value={form.ruc_transportista} onChange={(e) => setForm({ ...form, ruc_transportista: e.target.value })} /></div>
            <div><Label>Placa vehículo</Label><Input value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value })} placeholder="ABC-1234" /></div>
          </div>
          {form.destinatarios.map((d, di) => (
            <div key={`dest-${di}-${d.cliente_id || ""}`} className="border border-[#d1dbd6] rounded-md mt-4">
              <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
                <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Destinatario {di + 1}</div>
                {form.destinatarios.length > 1 && <Button size="sm" variant="ghost" onClick={() => setForm(f => ({ ...f, destinatarios: f.destinatarios.filter((_, x) => x !== di) }))}><Trash2 size={14} /></Button>}
              </div>
              <div className="p-3 grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label>Cliente *</Label>
                  <ClienteCombobox
                    value={d.cliente_id}
                    onChange={(v) => onCli(di, v)}
                    clientes={clientes}
                    placeholder="Buscar por razón social, RUC o ciudad…"
                    testId={`gr-cli-${di}`}
                  />
                </div>
                <div><Label>Motivo traslado</Label><Input value={d.motivo_traslado} onChange={(e) => updDest(di, "motivo_traslado", e.target.value)} /></div>
                <div><Label>Identificación</Label><Input value={d.identificacion} onChange={(e) => updDest(di, "identificacion", e.target.value)} /></div>
                <div className="col-span-2"><Label>Razón social</Label><Input value={d.razon_social} onChange={(e) => updDest(di, "razon_social", e.target.value)} /></div>
                <div className="col-span-3"><Label>Dirección destinatario</Label><Input value={d.direccion} onChange={(e) => updDest(di, "direccion", e.target.value)} /></div>

                <div className="col-span-3 bg-[#e9f2ee] border border-[#b2d0c2] rounded p-3">
                  <Label className="text-xs uppercase tracking-wider text-[#205c45] font-semibold">Factura sustento (asociada al cliente)</Label>
                  {d.cliente_id && facturasPorCliente(d.cliente_id).length > 0 ? (
                    <FacturaCombobox
                      value={d.factura_sustento_id}
                      onChange={(v) => onSelectFacturaSustento(di, v)}
                      facturas={facturasPorCliente(d.cliente_id)}
                      placeholder="Buscar factura del cliente — auto-carga sus productos…"
                      testId={`gr-factura-${di}`}
                      className="mt-1"
                    />
                  ) : (
                    <div className="text-xs text-[#5c7368] italic mt-1">
                      {!d.cliente_id ? "Primero elija un cliente" : "Cliente sin facturas; ingrese manual y agregue productos uno por uno"}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    <div><Label className="text-xs">N° Factura sustento</Label><Input value={d.num_doc_sustento} onChange={(e) => updDest(di, "num_doc_sustento", e.target.value)} placeholder="001-002-000000001" /></div>
                    <div><Label className="text-xs">Fecha factura</Label><Input type="date" value={d.fecha_emision_doc_sustento} onChange={(e) => updDest(di, "fecha_emision_doc_sustento", e.target.value)} /></div>
                    <div><Label className="text-xs">Ruta</Label><Input value={d.ruta} onChange={(e) => updDest(di, "ruta", e.target.value)} placeholder="Opcional" /></div>
                  </div>
                </div>
              </div>
              <div className="px-3 py-2 bg-[#f7f9f8] border-t flex justify-between items-center">
                <div className="text-xs font-semibold text-[#5c7368] uppercase tracking-wider">Productos transportados</div>
                <Button size="sm" variant="outline" onClick={() => addLinea(di)}><Plus size={12} className="mr-1" />Agregar</Button>
              </div>
              <table className="erp-table">
                <thead><tr><th>Producto</th><th className="w-24">Código</th><th className="w-24 text-right">Cantidad</th><th className="w-10"></th></tr></thead>
                <tbody>
                  {d.detalles.map((l, li) => (
                    <tr key={`gr-${di}-${li}-${l.producto || l.codigo_interno || ""}`}>
                      <td>
                        <ProductoCombobox
                          value={l.producto || ""}
                          onChange={(v) => updLinea(di, li, "producto", v)}
                          productos={prods}
                          placeholder="Buscar producto…"
                          testId={`gr-prod-${di}-${li}`}
                        />
                      </td>
                      <td><Input className="h-9 font-mono text-xs" value={l.codigo_interno} onChange={(e) => updLinea(di, li, "codigo_interno", e.target.value)} /></td>
                      <td><Input className="h-9 text-right" type="number" value={l.cantidad} onChange={(e) => updLinea(di, li, "cantidad", parseFloat(e.target.value) || 0)} /></td>
                      <td><Button size="icon" variant="ghost" onClick={() => setForm(f => { const dd = [...f.destinatarios]; dd[di] = { ...dd[di], detalles: dd[di].detalles.filter((_, x) => x !== li) }; return { ...f, destinatarios: dd }; })}><Trash2 size={14} /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <div className="flex justify-start mt-2">
            <Button size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, destinatarios: [...f.destinatarios, { cliente_id: "", identificacion: "", razon_social: "", direccion: "", motivo_traslado: "Venta", ruta: "", cod_doc_sustento: "01", num_doc_sustento: "", factura_sustento_id: "", fecha_emision_doc_sustento: new Date().toISOString().slice(0,10), detalles: [] }] }))}>
              <Plus size={12} className="mr-1" />Agregar otro destinatario
            </Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-gr-btn">Generar guía</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
